import { useState, useCallback, useMemo, useEffect } from 'react';
import { CheckCircle2, AlertCircle, ExternalLink, Info, X, Trash2, Shield } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { apiClient } from '@/lib/api';
import { TRACKING_SERVICES, detectActiveTrackingServices, removeTrackingSnippet, type TrackingService } from './tracking-config';

interface TrackingWizardModalProps {
  service: TrackingService;
  siteGroupId?: string | null;
  pageId?: string | null;
  mode: 'page' | 'project';
  customHeadCode: string;
  onCustomHeadCodeChange: (code: string) => void;
  onProjectSnippetChanged?: () => void;
  onClose: () => void;
}

export default function TrackingWizardModal({
  service,
  siteGroupId,
  pageId,
  mode,
  customHeadCode,
  onCustomHeadCodeChange,
  onProjectSnippetChanged,
  onClose,
}: TrackingWizardModalProps) {
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardId, setWizardId] = useState('');
  const [wizardSaving, setWizardSaving] = useState(false);
  const [wizardError, setWizardError] = useState('');
  const [wizardScope, setWizardScope] = useState<'project' | 'page'>(siteGroupId ? 'project' : 'page');

  const wizardValid = service.validationRegex.test(wizardId.trim());

  const handleWizardSave = useCallback(async () => {
    if (!wizardValid) return;
    setWizardSaving(true);
    setWizardError('');

    const trackingId = wizardId.trim();
    const snippet = service.fn(trackingId);

    try {
      if (wizardScope === 'project' && siteGroupId) {
        const current = await apiClient.get<{ custom_head_code?: string; custom_body_code?: string }>(`/landing-pages/site-group/${siteGroupId}/snippets`);
        const snippetData = current?.data ?? {};
        const existingHead = snippetData.custom_head_code || '';
        const existingBody = snippetData.custom_body_code || '';
        const cleanedHead = removeTrackingSnippet(existingHead, service.id);
        const newHead = cleanedHead ? `${cleanedHead}\n${snippet}` : snippet;
        await apiClient.put(`/landing-pages/site-group/${siteGroupId}/snippets`, {
          custom_head_code: newHead,
          custom_body_code: existingBody,
        });
        onProjectSnippetChanged?.();
      } else if (pageId) {
        const pageRes = await apiClient.get<{ settings?: { custom_head_code?: string }; sections?: unknown[]; seo_meta?: Record<string, unknown> }>(`/landing-pages/${pageId}`);
        const pageData = pageRes?.data ?? null;
        const existingSettings = pageData?.settings || {};
        const existingHead = existingSettings.custom_head_code || '';
        const cleanedHead = removeTrackingSnippet(existingHead, service.id);
        const newHead = cleanedHead ? `${cleanedHead}\n${snippet}` : snippet;
        await apiClient.put(`/landing-pages/${pageId}`, {
          sections: pageData?.sections || [],
          seo_meta: pageData?.seo_meta || {},
          settings: { ...existingSettings, custom_head_code: newHead },
        });
        onCustomHeadCodeChange(newHead);
      }
      setWizardStep(2);
    } catch (err) {
      setWizardError((err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Errore durante il salvataggio');
    } finally {
      setWizardSaving(false);
    }
  }, [service, wizardId, wizardValid, siteGroupId, pageId, wizardScope, onCustomHeadCodeChange, onProjectSnippetChanged]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl" style={{ backgroundColor: `${service.color}15` }}>
              {service.icon}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{service.label}</h3>
              <p className="text-xs text-gray-500">
                {wizardStep === 0 ? 'Documentazione' : wizardStep === 1 ? 'Inserisci ID' : 'Completato'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <div className="flex items-center gap-1 px-4 pt-3">
          {[0, 1, 2].map(step => (
            <div key={step} className={`flex-1 h-1 rounded-full transition-colors ${step <= wizardStep ? 'bg-sky-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
          ))}
        </div>

        <div className="p-4">
          {wizardStep === 0 && (
            <div className="space-y-4">
              <div className="p-4 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg">
                <div className="flex items-start gap-2 mb-2">
                  <Info className="h-4 w-4 text-sky-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-sky-800 dark:text-sky-300 mb-1">Cos'è {service.label}?</p>
                    <p className="text-xs text-sky-700 dark:text-sky-400">{service.description}</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">Come trovare il tuo {service.idLabel}:</p>
                <ol className="space-y-2">
                  {service.findIdSteps.map((step, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <span className="w-5 h-5 rounded-full bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
                <a
                  href={service.dashboardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-xs text-sky-600 hover:text-sky-700 font-medium"
                >
                  Apri {service.label} <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              {service.notes && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    {service.notes}
                  </p>
                </div>
              )}

              <Button onClick={() => setWizardStep(1)} size="sm" className="bg-sky-500 hover:bg-sky-600 text-white w-full">
                Ho trovato il mio {service.idLabel} →
              </Button>
            </div>
          )}

          {wizardStep === 1 && (
            <div className="space-y-4">
              {siteGroupId && pageId && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Applica a</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setWizardScope('project')}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                        wizardScope === 'project'
                          ? 'bg-sky-500 text-white border-sky-500'
                          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-sky-400'
                      }`}
                    >
                      Tutto il progetto
                    </button>
                    <button
                      onClick={() => setWizardScope('page')}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                        wizardScope === 'page'
                          ? 'bg-sky-500 text-white border-sky-500'
                          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-sky-400'
                      }`}
                    >
                      Solo questa pagina
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{service.idLabel}</label>
                <input
                  type="text"
                  value={wizardId}
                  onChange={e => setWizardId(service.id === 'ga4' || service.id === 'gtm' ? e.target.value.toUpperCase() : e.target.value)}
                  placeholder={service.placeholder}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-inset focus:ring-sky-500 focus:border-transparent"
                  autoFocus
                />
                <p className="text-xs text-gray-400 mt-1">{service.validationHint}</p>
                <p className="text-[10px] text-gray-400 mt-0.5 italic">{service.instructions}</p>
              </div>

              {wizardId && !wizardValid && (
                <p className="text-xs text-red-500">Formato non valido. {service.validationHint}</p>
              )}

              {wizardId && wizardValid && (
                <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Snippet che verrà aggiunto nel <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{'<head>'}</code> {wizardScope === 'project' && siteGroupId ? 'di tutte le pagine del progetto' : 'di questa pagina'}:
                  </p>
                  <pre className="text-[10px] text-gray-500 overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto">
                    {service.fn(wizardId.trim())}
                  </pre>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setWizardStep(0)}>← Indietro</Button>
                <Button onClick={handleWizardSave} disabled={!wizardValid || wizardSaving} size="sm" className="bg-sky-500 hover:bg-sky-600 text-white flex-1">
                  {wizardSaving ? 'Salvataggio...' : 'Salva e Attiva'}
                </Button>
              </div>

              {wizardError && (
                <p className="text-xs text-red-500">{wizardError}</p>
              )}
            </div>
          )}

          {wizardStep === 2 && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{service.label} Configurato!</p>
                <p className="text-xs text-gray-500 mt-1">
                  Lo snippet con {service.idLabel} <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{wizardId.trim()}</code> è stato salvato {wizardScope === 'project' && siteGroupId ? 'nel progetto' : 'nella pagina'}.
                </p>
              </div>

              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-xs font-semibold text-green-800 dark:text-green-300 mb-2">Come verificare che funzioni:</p>
                <ol className="space-y-1.5">
                  {service.verifySteps.map((step, i) => (
                    <li key={i} className="text-xs text-green-700 dark:text-green-400 flex items-start gap-2">
                      <span className="w-4 h-4 rounded-full bg-green-200 dark:bg-green-800 text-green-700 dark:text-green-300 flex items-center justify-center text-[9px] font-bold flex-shrink-0">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
                <a
                  href={service.dashboardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-xs text-green-700 dark:text-green-400 hover:text-green-800 font-medium"
                >
                  Apri dashboard {service.label} <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              <Button onClick={onClose} size="sm" className="w-full bg-sky-500 hover:bg-sky-600 text-white">
                Chiudi
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface TrackingServiceCardsProps {
  customHeadCode: string;
  onCustomHeadCodeChange: (code: string) => void;
  siteGroupId?: string | null;
  pageId?: string | null;
  mode: 'page' | 'project';
}

export function TrackingServiceCards({
  customHeadCode,
  onCustomHeadCodeChange,
  siteGroupId,
  pageId,
  mode,
}: TrackingServiceCardsProps) {
  const [selectedService, setSelectedService] = useState<TrackingService | null>(null);
  const [removingService, setRemovingService] = useState<string | null>(null);
  const [projectHeadCode, setProjectHeadCode] = useState('');

  useEffect(() => {
    if (siteGroupId) {
      apiClient.get<{ custom_head_code?: string; custom_body_code?: string }>(`/landing-pages/site-group/${siteGroupId}/snippets`)
        .then((res) => {
          // Boundary difensivo: la risposta snippets è "nuda" (non wrappata in
          // { success, data }) su alcune superfici → tollera entrambe le forme.
          const data = (res?.data ?? (res as unknown as { custom_head_code?: string } | undefined) ?? {}) as { custom_head_code?: string };
          setProjectHeadCode(data.custom_head_code || '');
        })
        .catch(() => setProjectHeadCode(''));
    } else {
      setProjectHeadCode('');
    }
  }, [siteGroupId]);

  const combinedHeadCode = useMemo(() => {
    if (projectHeadCode && customHeadCode) return `${projectHeadCode}\n${customHeadCode}`;
    return projectHeadCode || customHeadCode;
  }, [projectHeadCode, customHeadCode]);

  const activeTracking = useMemo(() => detectActiveTrackingServices(combinedHeadCode), [combinedHeadCode]);

  const refreshProjectSnippets = useCallback(async () => {
    if (!siteGroupId) return;
    try {
      const res = await apiClient.get<{ custom_head_code?: string }>(`/landing-pages/site-group/${siteGroupId}/snippets`);
      const data = res?.data ?? {};
      setProjectHeadCode(data.custom_head_code || '');
    } catch { /* ignore */ }
  }, [siteGroupId]);

  const handleRemoveTracking = useCallback(async (serviceId: string) => {
    setRemovingService(serviceId);
    try {
      const service = TRACKING_SERVICES.find(s => s.id === serviceId);
      if (!service) return;

      const projectHasIt = projectHeadCode && detectActiveTrackingServices(projectHeadCode).some(a => a.serviceId === serviceId);
      const pageHasIt = customHeadCode && detectActiveTrackingServices(customHeadCode).some(a => a.serviceId === serviceId);

      if (projectHasIt && siteGroupId) {
        const current = await apiClient.get<{ custom_head_code?: string; custom_body_code?: string }>(`/landing-pages/site-group/${siteGroupId}/snippets`);
        const snippetData = current?.data ?? {};
        const existingHead = snippetData.custom_head_code || '';
        const existingBody = snippetData.custom_body_code || '';
        const cleanedHead = removeTrackingSnippet(existingHead, serviceId);
        await apiClient.put(`/landing-pages/site-group/${siteGroupId}/snippets`, {
          custom_head_code: cleanedHead,
          custom_body_code: existingBody,
        });
        setProjectHeadCode(cleanedHead);
      }

      if (pageHasIt && pageId) {
        const pageRes = await apiClient.get<{ settings?: { custom_head_code?: string }; sections?: unknown[]; seo_meta?: Record<string, unknown> }>(`/landing-pages/${pageId}`);
        const pageData = pageRes?.data ?? null;
        const existingSettings = pageData?.settings || {};
        const existingHead = existingSettings.custom_head_code || '';
        const cleanedHead = removeTrackingSnippet(existingHead, serviceId);
        await apiClient.put(`/landing-pages/${pageId}`, {
          sections: pageData?.sections || [],
          seo_meta: pageData?.seo_meta || {},
          settings: { ...existingSettings, custom_head_code: cleanedHead },
        });
        onCustomHeadCodeChange(cleanedHead);
      }
    } catch (err) {
      console.error('Failed to remove tracking:', err);
    } finally {
      setRemovingService(null);
    }
  }, [projectHeadCode, customHeadCode, siteGroupId, pageId, onCustomHeadCodeChange]);

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-sky-500" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Servizi di Tracking</h4>
          </div>
          {activeTracking.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
              {activeTracking.length} attiv{activeTracking.length === 1 ? 'o' : 'i'}
            </span>
          )}
        </div>

        {(!siteGroupId && !pageId) && (
          <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400">
            La configurazione tracking richiede un progetto o una pagina salvata.
          </div>
        )}

        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {TRACKING_SERVICES.map(service => {
            const active = activeTracking.find(a => a.serviceId === service.id);
            const isRemoving = removingService === service.id;
            const inProject = projectHeadCode ? detectActiveTrackingServices(projectHeadCode).some(a => a.serviceId === service.id) : false;
            const inPage = customHeadCode ? detectActiveTrackingServices(customHeadCode).some(a => a.serviceId === service.id) : false;
            const scopeLabel = inProject && inPage ? 'progetto + pagina' : inProject ? 'progetto' : inPage ? 'pagina' : '';

            return (
              <div key={service.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0" style={{ backgroundColor: `${service.color}15` }}>
                  {service.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{service.label}</span>
                    {active && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                        <CheckCircle2 className="h-2.5 w-2.5" /> {active.trackingId}
                      </span>
                    )}
                    {active && scopeLabel && (
                      <span className="text-[9px] text-gray-400">{scopeLabel}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {active ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedService(service)}
                        className="text-xs text-sky-600 hover:text-sky-700 h-7 px-2"
                      >
                        Modifica
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveTracking(service.id)}
                        disabled={isRemoving}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 h-7 px-1.5"
                      >
                        {isRemoving ? '...' : <Trash2 className="h-3 w-3" />}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => setSelectedService(service)}
                      disabled={!siteGroupId && !pageId}
                      className="bg-sky-500 hover:bg-sky-600 text-white text-xs h-7 px-3"
                    >
                      Configura
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedService && (
        <TrackingWizardModal
          service={selectedService}
          siteGroupId={siteGroupId}
          pageId={pageId}
          mode={mode}
          customHeadCode={customHeadCode}
          onCustomHeadCodeChange={onCustomHeadCodeChange}
          onProjectSnippetChanged={refreshProjectSnippets}
          onClose={() => setSelectedService(null)}
        />
      )}
    </>
  );
}
